import React, { ChangeEvent, Suspense, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import addDays from 'date-fns/addDays';

import { DateVariant, RadioGroup, Stack } from '@lyearn/ui';
import DatePicker from '@/components/DatePicker';
import NumberInput from '@/components/NumberInput';
import Radio from '@/components/Radio';
import {
  AssignmentDueDateEnum,
  DueDateAssignmentConfigInput,
  RelativeDateFilterOperatorEnum,
} from '@/types/schema';

import ConditionalDueDateSelector from './ConditionalDueDateSelector';

const DEFAULT_DAYS = 7;

export interface DueDateProps {
  value?: DueDateAssignmentConfigInput | null;
  onChange: (value: DueDateAssignmentConfigInput) => void;
  disabled?: boolean;
  className?: string;
}

function DueDate(props: DueDateProps) {
  const { value, onChange, className } = props;
  const disabled = false;
  const { t } = useTranslation();
  const resolvedDueDate = useMemo(() => {
    if (value?.dueDate) {
      return new Date(value.dueDate);
    }

    return addDays(new Date(), DEFAULT_DAYS);
  }, [value?.dueDate]);

  const resolvedValue = value?.value ?? DEFAULT_DAYS;
  const resolvedOperator = value?.operator ?? RelativeDateFilterOperatorEnum.Next;

  const onDueDateTypeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, type: string) => {
      onChange({
        ...value,
        dueDateType: type as AssignmentDueDateEnum,
        value: resolvedValue,
        dueDate: (resolvedDueDate as Date).toISOString(),
      });
    },
    [onChange, resolvedDueDate, resolvedValue, value],
  );

  const onDueDateChange = useCallback(
    (date: any) => {
      if (value) {
        onChange({
          ...value,
          dueDate: (date as Date).toISOString(),
        });
      }
    },
    [onChange, value],
  );

  const onIntervalChange = useCallback(
    (val: number) => {
      if (value) {
        onChange({
          ...value,
          value: val,
        });
      }
    },
    [onChange, value],
  );

  const onOperatorChange = useCallback(
    (val: RelativeDateFilterOperatorEnum) => {
      if (value) {
        onChange({
          ...value,
          operator: val,
          value: val === RelativeDateFilterOperatorEnum.This ? 0 : value.value,
        });
      }
    },
    [onChange, value],
  );

  const onTagChange = useCallback(
    (val?: string) => {
      if (value) {
        onChange({
          ...value,
          tagId: val,
        });
      }
    },
    [onChange, value],
  );

  return (
    <Stack className={className ?? 'w-full'} direction="col" gapY={8}>
      <RadioGroup
        aria-label="dueDateType"
        name="dueDateType"
        value={value?.dueDateType}
        onChange={onDueDateTypeChange}>
        <Radio
          disabled={disabled}
          label={t('AssignmentCreator:NeverDue')}
          value={AssignmentDueDateEnum.NoDueDate}
        />
        <Radio
          disabled={disabled}
          label={t('AssignmentCreator:DueOnSpecificDate')}
          value={AssignmentDueDateEnum.Fixed}
        />
        {value?.dueDateType === AssignmentDueDateEnum.Fixed ? (
          <Stack className="mt-4 mb-12 w-256 ms-26">
            <DatePicker
              pickerProps={{
                variant: DateVariant.DateAndTime,
                selectedTime: resolvedDueDate,
                onChange: onDueDateChange,
                disablePast: true,
              }}
            />
          </Stack>
        ) : null}
        <Radio
          disabled={disabled}
          label={t('AssignmentCreator:DueAfterAnIntervalOfDays')}
          value={AssignmentDueDateEnum.Dynamic}
        />
        {value?.dueDateType === AssignmentDueDateEnum.Dynamic ? (
          <Stack className="mt-4 mb-12 w-full ms-26">
            <NumberInput
              canBeEmpty
              className="w-256"
              disabled={disabled}
              inputSuffix={t('Common:day', { count: resolvedValue })}
              max={999}
              min={0}
              value={resolvedValue}
              onChange={onIntervalChange}
            />
          </Stack>
        ) : null}
        <Radio
          disabled={disabled}
          label={t('AssignmentCreator:ConditionalDueDate')}
          value={AssignmentDueDateEnum.Relative}
        />
        {value?.dueDateType === AssignmentDueDateEnum.Relative ? (
          <Suspense fallback={null}>
            <ConditionalDueDateSelector
              disabled={disabled}
              operator={resolvedOperator}
              tagId={value?.tagId}
              value={resolvedValue}
              onIntervalChange={onIntervalChange}
              onOperatorChange={onOperatorChange}
              onTagChange={onTagChange}
            />
          </Suspense>
        ) : null}
      </RadioGroup>
    </Stack>
  );
}

export default React.memo(DueDate);
